"use client";

import * as React from "react";
import {
  ArrowDown,
  ArrowUp,
  ChevronsUpDown,
  Download,
  LayoutGrid,
  Mail,
  MoreHorizontal,
  Rows3,
  Search,
  X,
} from "lucide-react";
import {
  Badge,
} from "@/components/ui/badge";
import {
  Button,
} from "@/components/ui/button";
import {
  Checkbox,
} from "@/components/ui/checkbox";
import {
  Input,
} from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@/components/ui/toggle-group";
import {
  TableSkeleton,
} from "@/components/state/skeletons";
import {
  NoCertificates,
  NoSearchResults,
} from "@/components/state/empty-states";
import {
  FetchError,
} from "@/components/state/error-states";
import {
  cn,
} from "@/lib/utils";

export type CertStatus = "Valid" | "Expiring soon" | "Expired" | "Revoked";

export type CertificateRow = {
  id: string;
  name: string;
  email: string;
  competency: string;
  status: CertStatus;
  issuedAt: string;
  expiresAt: string;
  emailSentAt: string | null;
  verificationViews: number;
  shortUrl: string;
  /** Certificate artwork URL, if one was attached. Absent -> no "Download certificate". */
  imageURI: string | null;
};

export type SortKey = "name" | "issuedAt" | "expiresAt";
export type DateRange = "any" | "30d" | "90d" | "year" | "last-year";
export type EmailFilter = "any" | "sent" | "not-sent";

export type CertificateQuery = {
  q: string;
  status: CertStatus | "all";
  competency: string | "all";
  range: DateRange;
  email: EmailFilter;
  sort: { key: SortKey; dir: "asc" | "desc" };
  page: number;
};

export const EMPTY_QUERY: CertificateQuery = {
  q: "",
  status: "all",
  competency: "all",
  range: "any",
  email: "any",
  sort: { key: "issuedAt", dir: "desc" },
  page: 1,
};

const RANGE_LABEL: Record<DateRange, string> = {
  any: "Any date",
  "30d": "Last 30 days",
  "90d": "Last 90 days",
  year: "This year",
  "last-year": "Last year",
};

const EMAIL_LABEL: Record<EmailFilter, string> = {
  any: "Any",
  sent: "Sent",
  "not-sent": "Not sent",
};

const STATUS_CLASS: Record<CertStatus, string> = {
  Valid: "border-success-border bg-success text-success-foreground",
  "Expiring soon": "border-warning-border bg-warning text-warning-foreground",
  Expired: "border-neutral-border bg-neutral text-neutral-foreground",
  Revoked: "border-danger-border bg-danger text-danger-foreground",
};

export function StatusBadge({ status }: { status: CertStatus }) {
  return (
    <Badge
      variant="outline"
      className={cn(
        "gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium",
        STATUS_CLASS[status]
      )}
    >
      <span className="size-1.5 rounded-full bg-current" aria-hidden />
      {status}
    </Badge>
  );
}

/** 300ms debounce so search-as-you-type doesn't fire per keystroke. */
export function useDebounced<T>(value: T, delay = 300) {
  const [v, setV] = React.useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

function SortableHead({
  label,
  sortKey,
  query,
  onSort,
  className,
}: {
  label: string;
  sortKey: SortKey;
  query: CertificateQuery;
  onSort: (k: SortKey) => void;
  className?: string;
}) {
  const active = query.sort.key === sortKey;
  const Icon = !active ? ChevronsUpDown : query.sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        aria-sort={active ? (query.sort.dir === "asc" ? "ascending" : "descending") : "none"}
        className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.1em]"
      >
        {label}
        <Icon className={cn("size-3", !active && "opacity-40")} />
      </button>
    </TableHead>
  );
}

function FilterChips({
  query,
  onChange,
  onClearAll,
}: {
  query: CertificateQuery;
  onChange: (patch: Partial<CertificateQuery>) => void;
  onClearAll: () => void;
}) {
  const chips: { label: string; clear: () => void }[] = [];
  if (query.q) chips.push({ label: `Search: “${query.q}”`, clear: () => onChange({ q: "" }) });
  if (query.status !== "all")
    chips.push({ label: `Status: ${query.status}`, clear: () => onChange({ status: "all" }) });
  if (query.competency !== "all")
    chips.push({
      label: `Competency: ${query.competency}`,
      clear: () => onChange({ competency: "all" }),
    });
  if (query.range !== "any")
    chips.push({ label: `Issued: ${RANGE_LABEL[query.range]}`, clear: () => onChange({ range: "any" }) });
  if (query.email !== "any")
    chips.push({ label: `Email sent: ${EMAIL_LABEL[query.email]}`, clear: () => onChange({ email: "any" }) });

  if (chips.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 border-b px-6 py-3">
      <span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
        Filters
      </span>
      {chips.map((c) => (
        <span
          key={c.label}
          className="flex h-7 items-center gap-1.5 rounded-full border border-accent-foreground/20 bg-accent pl-2.5 pr-1.5 text-[13px] text-accent-foreground"
        >
          <span className="max-w-[28ch] truncate">{c.label}</span>
          <button
            type="button"
            onClick={c.clear}
            aria-label={`Remove ${c.label}`}
            className="flex size-4 items-center justify-center rounded-full hover:bg-accent-foreground/15"
          >
            <X className="size-2.5" />
          </button>
        </span>
      ))}
      <button
        type="button"
        onClick={onClearAll}
        className="ml-1 text-[13px] font-medium text-primary hover:underline"
      >
        Clear all
      </button>
    </div>
  );
}

export function CertificatesTable({
  rows,
  total,
  filteredTotal,
  competencies,
  query,
  onQueryChange,
  view,
  onViewChange,
  selectedIds,
  onSelectedChange,
  onOpenRow,
  onRevokeRow,
  onCopyLinkRow,
  onDownloadCertificateRow,
  onDownloadQrRow,
  onResendRow,
  onBulkEmail,
  onBulkDownload,
  onBulkExport,
  canIssue,
  isLoading,
  error,
  onRetry,
  pageSize = 25,
  renderGrid,
}: {
  rows: CertificateRow[];
  /** Unfiltered count — always visible. */
  total: number;
  filteredTotal: number;
  competencies: string[];
  query: CertificateQuery;
  onQueryChange: (patch: Partial<CertificateQuery>) => void;
  view: "table" | "grid";
  onViewChange: (v: "table" | "grid") => void;
  selectedIds: string[];
  onSelectedChange: (ids: string[]) => void;
  onOpenRow: (id: string) => void;
  /** Opens the revoke confirmation. Omit to hide the action. */
  onRevokeRow?: (id: string) => void;
  onCopyLinkRow?: (id: string) => void;
  /** Downloads the certificate artwork image. */
  onDownloadCertificateRow?: (id: string) => void;
  /** Downloads the branded Sigalmark hexagon QR. */
  onDownloadQrRow?: (id: string) => void;
  onResendRow?: (id: string) => void;
  onBulkEmail?: () => void;
  onBulkDownload?: () => void;
  onBulkExport?: () => void;
  canIssue: boolean;
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  pageSize?: number;
  /** Card grid, rendered behind the list/grid toggle. */
  renderGrid?: (rows: CertificateRow[]) => React.ReactNode;
}) {
  const [qInput, setQInput] = React.useState(query.q);
  const debouncedQ = useDebounced(qInput);
  React.useEffect(() => {
    if (debouncedQ !== query.q) onQueryChange({ q: debouncedQ, page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ]);

  const isFiltered =
    !!query.q ||
    query.status !== "all" ||
    query.competency !== "all" ||
    query.range !== "any" ||
    query.email !== "any";

  const allChecked = rows.length > 0 && rows.every((r) => selectedIds.includes(r.id));
  const clearAll = () => {
    setQInput("");
    onQueryChange({ ...EMPTY_QUERY, sort: query.sort });
  };

  const from = (query.page - 1) * pageSize + 1;
  const to = Math.min(query.page * pageSize, filteredTotal);

  return (
    <div className="overflow-hidden rounded-[var(--radius)] border bg-card">
      {/* filter bar */}
      <div className="flex flex-wrap items-center gap-2 border-b p-4 px-6">
        <div className="relative w-[300px] max-w-full">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            className="h-9 bg-background pl-9"
            placeholder="Search name, competency or email"
            aria-label="Search certificates"
          />
        </div>

        <Select
          value={query.status}
          onValueChange={(v) => onQueryChange({ status: v as CertStatus | "all", page: 1 })}
        >
          <SelectTrigger className="h-9 w-[150px] bg-background">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Status: All</SelectItem>
            {(["Valid", "Expiring soon", "Expired", "Revoked"] as CertStatus[]).map((s) => (
              <SelectItem key={s} value={s}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={query.competency}
          onValueChange={(v) => onQueryChange({ competency: v, page: 1 })}
        >
          <SelectTrigger className="h-9 w-[190px] bg-background">
            <SelectValue placeholder="Competency" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Competency: All</SelectItem>
            {competencies.map((c) => (
              <SelectItem key={c} value={c}>
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={query.range}
          onValueChange={(v) => onQueryChange({ range: v as DateRange, page: 1 })}
        >
          <SelectTrigger className="h-9 w-[150px] bg-background">
            <SelectValue placeholder="Issued" />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(RANGE_LABEL) as DateRange[]).map((r) => (
              <SelectItem key={r} value={r}>
                {r === "any" ? "Issued: Any date" : RANGE_LABEL[r]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={query.email}
          onValueChange={(v) => onQueryChange({ email: v as EmailFilter, page: 1 })}
        >
          <SelectTrigger className="h-9 w-[150px] bg-background">
            <SelectValue placeholder="Email sent" />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(EMAIL_LABEL) as EmailFilter[]).map((e) => (
              <SelectItem key={e} value={e}>
                {e === "any" ? "Email sent: Any" : EMAIL_LABEL[e]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex-1" />

        <ToggleGroup
          type="single"
          value={view}
          onValueChange={(v) => v && onViewChange(v as "table" | "grid")}
          className="rounded-[var(--radius)] border bg-background p-[3px]"
        >
          <ToggleGroupItem value="table" aria-label="Table view" className="size-8 rounded-md">
            <Rows3 className="size-4" />
          </ToggleGroupItem>
          <ToggleGroupItem value="grid" aria-label="Grid view" className="size-8 rounded-md">
            <LayoutGrid className="size-4" />
          </ToggleGroupItem>
        </ToggleGroup>
      </div>

      <FilterChips query={query} onChange={onQueryChange} onClearAll={clearAll} />

      {/* bulk bar */}
      {selectedIds.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 border-b bg-accent px-6 py-3">
          <span className="text-[13px] font-semibold tabular-nums text-accent-foreground">
            {selectedIds.length} selected
          </span>
          <button
            type="button"
            onClick={() => onSelectedChange([])}
            className="text-[13px] text-accent-foreground/80 underline"
          >
            Clear
          </button>
          <div className="flex-1" />
          <Button variant="outline" size="sm" className="gap-2" onClick={onBulkEmail}>
            <Mail className="size-3.5" />
            Send issuance email
          </Button>
          <Button variant="outline" size="sm" className="gap-2" onClick={onBulkDownload}>
            <Download className="size-3.5" />
            Download
          </Button>
          <Button variant="outline" size="sm" onClick={onBulkExport}>
            Export CSV
          </Button>
        </div>
      )}

      {error ? (
        <FetchError onRetry={onRetry} />
      ) : isLoading ? (
        <TableSkeleton rows={10} numericColumns={3} />
      ) : rows.length === 0 ? (
        isFiltered ? (
          <NoSearchResults query={query.q || "these filters"} onClear={clearAll} />
        ) : (
          <NoCertificates canIssue={canIssue} />
        )
      ) : view === "grid" ? (
        <div className="p-6">{renderGrid?.(rows)}</div>
      ) : (
        // table-fixed distributes leftover width, and every other column has a
        // fixed size totalling 854px. Below that the Name column - which had no
        // width of its own - collapsed to zero and its header overlapped
        // Competency. Scroll horizontally instead of letting columns vanish.
        <div className="w-full overflow-x-auto">
        <Table className="table-fixed min-w-[940px]">
          <TableHeader className="sticky top-0 z-10 bg-muted">
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-11 pl-6">
                <Checkbox
                  checked={allChecked}
                  aria-label="Select all on this page"
                  onCheckedChange={(v) =>
                    onSelectedChange(v ? rows.map((r) => r.id) : [])
                  }
                />
              </TableHead>
              <SortableHead label="Name" sortKey="name" query={query} onSort={(k) => sortBy(k, query, onQueryChange)} className="w-[240px]" />
              <TableHead className="w-[260px] text-[11px] font-semibold uppercase tracking-[0.1em]">
                Competency
              </TableHead>
              <TableHead className="w-[140px] text-[11px] font-semibold uppercase tracking-[0.1em]">
                Status
              </TableHead>
              <SortableHead label="Issued" sortKey="issuedAt" query={query} onSort={(k) => sortBy(k, query, onQueryChange)} className="w-[118px]" />
              <SortableHead label="Expires" sortKey="expiresAt" query={query} onSort={(k) => sortBy(k, query, onQueryChange)} className="w-[118px]" />
              <TableHead className="w-[118px] text-[11px] font-semibold uppercase tracking-[0.1em]">
                Email sent
              </TableHead>
              <TableHead className="w-14 pr-6" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow
                key={r.id}
                onClick={() => onOpenRow(r.id)}
                className="h-12 cursor-pointer"
              >
                <TableCell className="pl-6" onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={selectedIds.includes(r.id)}
                    aria-label={`Select ${r.name}`}
                    onCheckedChange={(v) =>
                      onSelectedChange(
                        v
                          ? [...selectedIds, r.id]
                          : selectedIds.filter((id) => id !== r.id)
                      )
                    }
                  />
                </TableCell>
                <TableCell>
                  <div className="flex min-w-0 flex-col">
                    <span className="truncate text-sm font-medium">{r.name}</span>
                    <span className="truncate text-xs text-muted-foreground">{r.email}</span>
                  </div>
                </TableCell>
                <TableCell className="truncate text-[13px]">{r.competency}</TableCell>
                <TableCell>
                  <StatusBadge status={r.status} />
                </TableCell>
                <TableCell className="whitespace-nowrap text-[13px]">{r.issuedAt}</TableCell>
                <TableCell
                  className={cn(
                    "whitespace-nowrap text-[13px]",
                    r.status === "Expired"
                      ? "text-destructive"
                      : r.status === "Expiring soon"
                        ? "text-warning-foreground"
                        : "text-muted-foreground"
                  )}
                >
                  {r.expiresAt}
                </TableCell>
                <TableCell
                  className={cn(
                    "whitespace-nowrap text-[13px]",
                    r.emailSentAt ? "text-foreground/80" : "text-muted-foreground/70"
                  )}
                >
                  {r.emailSentAt ? `Sent ${r.emailSentAt}` : "Not sent"}
                </TableCell>
                <TableCell className="pr-6 text-right" onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-8"
                        aria-label={`Actions for ${r.name}`}
                      >
                        <MoreHorizontal className="size-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-52">
                      <DropdownMenuItem onSelect={() => onOpenRow(r.id)}>
                        Open details
                      </DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => onCopyLinkRow?.(r.id)}>
                        Copy verification link
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={() => onDownloadCertificateRow?.(r.id)}
                      >
                        Download certificate
                      </DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => onDownloadQrRow?.(r.id)}>
                        Download QR code
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        disabled={!r.email}
                        onSelect={() => onResendRow?.(r.id)}
                      >
                        {r.emailSentAt ? "Resend issuance email" : "Send issuance email"}
                      </DropdownMenuItem>
                      {onRevokeRow && r.status !== "Revoked" ? (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            variant="destructive"
                            onSelect={() => onRevokeRow(r.id)}
                          >
                            Revoke certificate
                          </DropdownMenuItem>
                        </>
                      ) : null}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </div>
      )}

      {/* pagination — total always visible */}
      {rows.length > 0 && (
        <div className="flex items-center justify-between gap-4 border-t px-6 py-4">
          <span className="text-[13px] tabular-nums text-muted-foreground">
            Showing {from}–{to} of {isFiltered ? `${filteredTotal} matching` : total}
          </span>
          <div className="flex items-center gap-2">
            <span className="text-[13px] tabular-nums text-muted-foreground">
              Page {query.page}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={query.page === 1}
              onClick={() => onQueryChange({ page: query.page - 1 })}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={to >= filteredTotal}
              onClick={() => onQueryChange({ page: query.page + 1 })}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function sortBy(
  key: SortKey,
  query: CertificateQuery,
  onQueryChange: (p: Partial<CertificateQuery>) => void
) {
  onQueryChange({
    sort:
      query.sort.key === key
        ? { key, dir: query.sort.dir === "asc" ? "desc" : "asc" }
        : { key, dir: key === "name" ? "asc" : "desc" },
    page: 1,
  });
}

/** ~280px card. Every element is per-record: no tenant logo, no unscannable QR. */
export function CertificateCard({
  row,
  selected,
  onToggle,
  onOpen,
  qrNode,
}: {
  row: CertificateRow;
  selected: boolean;
  onToggle: () => void;
  onOpen: () => void;
  /** The branded hexagon, supplied by the view so this stays presentational. */
  qrNode?: React.ReactNode;
}) {
  return (
    <div
      onClick={onOpen}
      className="flex min-h-[280px] cursor-pointer flex-col gap-4 rounded-[var(--radius)] border bg-background p-6 transition-colors hover:border-primary"
    >
      <div className="flex items-start gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="truncate text-base font-semibold">{row.name}</span>
          <span className="truncate text-[13px] text-muted-foreground">{row.competency}</span>
        </div>
        <span onClick={(e) => e.stopPropagation()}>
          <Checkbox checked={selected} onCheckedChange={onToggle} aria-label={`Select ${row.name}`} />
        </span>
      </div>

      <StatusBadge status={row.status} />

      <div className="flex items-start gap-4">
        <dl className="grid flex-1 grid-cols-2 gap-y-2 text-[13px]">
        <dt className="text-muted-foreground">Issued</dt>
        <dd className="text-right">{row.issuedAt}</dd>
        <dt className="text-muted-foreground">Expires</dt>
        <dd className="text-right">{row.expiresAt}</dd>
        <dt className="text-muted-foreground">Email</dt>
        <dd className={cn("text-right", !row.emailSentAt && "text-muted-foreground/70")}>
          {row.emailSentAt ? `Sent ${row.emailSentAt}` : "Not sent"}
        </dd>
        </dl>
        {qrNode ? <div className="shrink-0">{qrNode}</div> : null}
      </div>

      <div className="flex-1" />

      <div className="flex items-center gap-2 border-t pt-3">
        <span className="flex-1 text-[13px] tabular-nums text-muted-foreground">
          {row.verificationViews} verifications
        </span>
        <span className="text-[13px] font-medium text-primary">Details →</span>
      </div>
    </div>
  );
}
